import React, { memo, useMemo, useRef } from 'react';
import { Empty } from 'antd';
import { useTranslation } from 'react-i18next';
import { UIMap, UIMapMarker } from '@components/common/UIMap';
import { MAP_DEFAULT_CONFIG } from '@components/common/UIMap/utils/constants';
import Truck from 'src/assets/images/truck_large.svg';
import Hub from 'src/assets/images/hub_large.svg';
import Warehouse from 'src/assets/images/warehouse_large.svg';
import Delivery from 'src/assets/images/delivery.svg';
import Pickup from 'src/assets/images/pickup.svg';
import Rider from 'src/assets/images/rider_large.svg';
import RiderBlack from 'src/assets/images/Rider_Black.svg';
import RiderBlue from 'src/assets/images/Rider_Blue.svg';
import RiderGreen from 'src/assets/images/Rider_Green.svg';
import Pin from 'src/assets/images/pin_large.svg';

interface IMapViewVizProps {
  chartData: any;
}

interface IMarker {
  longitude: number;
  latitude: number;
  label?: string;
  description?: string;
  marker?: string;
}

interface IChartDataItem {
  coordinates?: [number, number];
  longitude?: number;
  lon?: number;
  lng?: number;
  x?: number;
  latitude?: number;
  lat?: number;
  y?: number;
  label?: string;
  name?: string;
  title?: string;
  description?: string;
  marker?: string;
}

interface IMarkerProps {
  label?: string;
  marker?: string;
}

export const mapMarkerConfig = {
  truck: {
    image: Truck,
    size: 50,
  },
  hub: {
    image: Hub,
    size: 50,
  },
  warehouse: {
    image: Warehouse,
    size: 50,
  },
  delivery: {
    image: Delivery,
    size: 50,
  },
  pickup: {
    image: Pickup,
    size: 50,
  },
  rider: {
    image: Rider,
    size: 50,
  },
  rider_black: {
    image: RiderBlack,
    size: 50,
  },
  rider_green: {
    image: RiderGreen,
    size: 50,
  },
  rider_blue: {
    image: RiderBlue,
    size: 50,
  },
  pin: {
    image: Pin,
    size: 50,
  },
};

const toArray = (value: any): any[] => {
  if (Array.isArray(value)) {
    return value;
  }
  if (value && typeof value === 'object') {
    if (Array.isArray(value.data)) {
      return value.data;
    }
    if (Array.isArray(value.points)) {
      return value.points;
    }
    if (Array.isArray(value.markers)) {
      return value.markers;
    }
    return Object.values(value);
  }
  return [];
};

const toNumber = (value: any): number | undefined => {
  if (typeof value === 'number') {
    return value;
  }
  if (typeof value === 'string') {
    const parsed = parseFloat(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  }
  return undefined;
};

const extractMarkers = (chartData: any): IMarker[] => {
  const dataset = toArray(chartData);
  return dataset
    .map((item: IChartDataItem): IMarker | null => {
      const coordinates = Array.isArray(item?.coordinates)
        ? item.coordinates
        : [
            item?.longitude ?? item?.lon ?? item?.lng ?? item?.x,
            item?.latitude ?? item?.lat ?? item?.y,
          ];
      const longitude = toNumber(coordinates?.[0]);
      const latitude = toNumber(coordinates?.[1]);
      if (!Number.isFinite(latitude) || !Number.isFinite(longitude)) {
        return null;
      }
      return {
        longitude,
        latitude,
        label: item?.label || item?.name || item?.title,
        description: item?.description,
        marker: item?.marker,
      };
    })
    .filter((marker): marker is IMarker => marker !== null);
};

const Marker: React.FC<IMarkerProps> = ({ label, marker }) => {
  const markerConfig =
    marker && mapMarkerConfig[marker as keyof typeof mapMarkerConfig];
  const size = markerConfig?.size || 25;

  if (markerConfig?.image) {
    return (
      <img
        src={markerConfig.image}
        alt={label || marker}
        title={label || undefined}
        width={size}
        height={size}
        style={{
          display: 'block',
        }}
      />
    );
  }

  return (
    <div
      title={label || undefined}
      style={{
        width: 14,
        height: 14,
        borderRadius: '50%',
        backgroundColor: '#1890ff',
        border: '2px solid #ffffff',
        boxShadow: '0 0 0 2px rgba(24, 144, 255, 0.35)',
      }}
    />
  );
};

const MapViewViz: React.FC<IMapViewVizProps> = (props) => {
  const { t } = useTranslation();
  const { chartData} = props;
  const mapRef = useRef(null);

  const markers = useMemo(() => extractMarkers(chartData), [chartData]);

  const initialViewState = useMemo(() => {
    if (!markers.length) {
      return { ...MAP_DEFAULT_CONFIG.initialViewState };
    }
    const { latitude, longitude } = markers.reduce(
      (accumulator, marker) => {
        return {
          latitude: accumulator.latitude + marker.latitude,
          longitude: accumulator.longitude + marker.longitude,
        };
      },
      { latitude: 0, longitude: 0 },
    );
    const averageLatitude = latitude / markers.length;
    const averageLongitude = longitude / markers.length;
    const zoom = 2;
    return {
      latitude: averageLatitude,
      longitude: averageLongitude,
      zoom,
    };
  }, [markers]);

  if (!markers.length) {
    return (
      <div
        style={{
          width: '100%',
          height: '100%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Empty description={t('No map data available')} />
      </div>
    );
  }

  return (
    <div style={{ width: '100%', height: '100%' }}>
      <UIMap mapRef={mapRef} initialViewState={initialViewState}>
        {markers.map((marker, index) => (
          <UIMapMarker
            key={`map-marker-${index}`}
            coordinates={[marker.longitude, marker.latitude]}
            anchor="center"
            popupHoverContent={
              marker.label ? (
                <div style={{ padding: '4px 8px' }}>{marker.label}</div>
              ) : undefined
            }
          >
            <Marker label={marker.label} marker={marker.marker} />
          </UIMapMarker>
        ))}
      </UIMap>
    </div>
  );
};

export default memo(MapViewViz);
